import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Check } from 'lucide-react';
import { fetchCreditStats } from '../store/reducers';
import { useLanguage, getSuccessPageTranslation } from '../hooks/useLanguage';

const API_BASE_URL = process.env.REACT_APP_DOCS_API_KEY;

const Success = () => {
  const dispatch = useDispatch();
  const [payment, setPayment] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const products = useSelector(state => state.products);
  const [isSpanish] = useLanguage();

  useEffect(() => {
    const sessionId = localStorage.getItem('stripeSessionId');
    if (!sessionId) {
      setError(getSuccessPageTranslation('noSessionId', isSpanish));
      setLoading(false);
      return;
    }
    const checkSessionStatus = async (sessionId) => {
      try {
        const response = await fetch(`https://${API_BASE_URL}/api/payments/status/${sessionId}`);
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || getSuccessPageTranslation('noPaymentFound', isSpanish));
        setPayment(data.payment);
        setStatus(data.stripeStatus || data.status || data.payment?.status);
        dispatch(fetchCreditStats());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    checkSessionStatus(sessionId);
  }, [dispatch, isSpanish]);

  if (loading) return <div className='text-center mt-20'>{getSuccessPageTranslation('loadingPaymentStatus', isSpanish)}</div>;
  if (error) return <div className="text-red-600 text-center mt-20">{error}</div>;
  if (!payment) return <div className='text-center mt-20'>{getSuccessPageTranslation('noPaymentFound', isSpanish)}</div>;

  // Find product by product_id in payment
  const product = products.find(p => p.productId === payment.product_id);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    const pad = (n) => n.toString().padStart(2, '0');
    let hours = d.getHours();
    const minutes = pad(d.getMinutes());
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}, ${pad(hours)}:${minutes} ${ampm}`;
  };

  return (
    <div className="flex justify-center items-center mt-20">
      <div className="bg-white border-container p-8 rounded-3xl max-w-1/2 text-center ">
        <div className="flex justify-center mb-4">
          <span className="inline-flex items-center justify-center w-16 h-16 rounded-full green-gradient-2">
            <Check className="w-10 h-10 text-white" />
          </span>
        </div>
        <h1 className="text-2xl font-bold mb-2 shine">{getSuccessPageTranslation('paymentSuccessful', isSpanish)}</h1>
        <p className="mb-4 text-gray-700">{getSuccessPageTranslation('thankYou', isSpanish)}</p>
        <div className='grid grid-cols-2 text-sm gap-8 mt-8'>
          <div className="text-left flex flex-col w-max">
            <span>{getSuccessPageTranslation('product', isSpanish)}</span>
            <span className='text-xl font-bold'>{product ? `${product.credits} ${getSuccessPageTranslation('credits', isSpanish)}` : payment.product_id || '-'}</span>
          </div>
          <div className="text-left flex flex-col w-max">
            <span>{getSuccessPageTranslation('amount', isSpanish)}</span>
            <span className='text-xl font-bold'>{payment.amount ? (payment.amount / 100).toFixed(2) : '-'} {payment.currency ? payment.currency.toUpperCase() : ''}</span>
          </div>
          <div className="text-left flex flex-col w-max">
            <span>{getSuccessPageTranslation('status', isSpanish)}</span>
            <span className='text-xl font-bold capitalize'>{status.split('_').join(' ')}</span>
          </div>
          <div className="text-left flex flex-col w-max">
            <span>{getSuccessPageTranslation('date', isSpanish)}</span>
            <span className='text-xl font-bold'>{formatDate(payment.date_created || payment.created_at)}</span>
          </div>
        </div>
        <a href="/" className="w-full inline-block mt-8 px-6 py-3 bg-default text-white rounded-full transition">{getSuccessPageTranslation('goToHome', isSpanish)}</a>
      </div>
    </div>
  );
};

export default Success;